import { useState, useMemo, useCallback } from 'react';
import { UnifiedTransaction } from '../types/payment';
import { inBusinessDays } from '../utils/businessDay';

export interface TransactionFilterState {
  globalSearch: string;
  createdBy: string;
  txType: string;
  status: string;
  processingStatus: string;
  processedBy: string;
  processorAssignedBy: string;
  amountExact: string;
  amountMin: string;
  amountMax: string;
  cardBrand: string;
  last4: string;
  bin: string;
  last4Ssn: string;
  dobYear: string;
  dobMonth: string;
  dobDay: string;
  email: string;
  dl: string;
  zip: string;
  city: string;
  billingState: string;
  address: string;
  phone: string;
  responseCode: string;
  category: string;
  saleState: string;
  tfn: string;
  dateFrom: string;
  dateTo: string;
  voidRefundStatus: string;
  voidedRefundedBy: string;
}

const initialFilterState: TransactionFilterState = {
  globalSearch: '',
  createdBy: '',
  txType: '',
  status: '',
  processingStatus: '',
  processedBy: '',
  processorAssignedBy: '',
  amountExact: '',
  amountMin: '',
  amountMax: '',
  cardBrand: '',
  last4: '',
  bin: '',
  last4Ssn: '',
  dobYear: '',
  dobMonth: '',
  dobDay: '',
  email: '',
  dl: '',
  zip: '',
  city: '',
  billingState: '',
  address: '',
  phone: '',
  responseCode: '',
  category: '',
  saleState: '',
  tfn: '',
  dateFrom: '',
  dateTo: '',
  voidRefundStatus: '',
  voidedRefundedBy: ''
};

export const useTransactionFilters = (transactions: UnifiedTransaction[]) => {
  const [filters, setFilters] = useState<TransactionFilterState>(initialFilterState);

  // Update individual filter
  const updateFilter = useCallback((field: keyof TransactionFilterState, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters(initialFilterState);
  }, []);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some(value => value !== '');
  }, [filters]);

  // Optimized filtering logic with memoization
  const filteredTransactions = useMemo(() => {
    if (!hasActiveFilters) return transactions;

    return transactions.filter(transaction => {
      // Global search - searches across all text fields
      if (filters.globalSearch) {
        const searchTerm = filters.globalSearch.toLowerCase().trim();
        if (searchTerm) {
          const searchableText = [
            transaction.id,
            transaction.created_by || '',
            transaction.first_name || '',
            transaction.last_name || '',
            transaction.tx_type || '',
            transaction.status || '',
            transaction.card_brand || '',
            transaction.last4 || '',
            transaction.last4_ssn || '',
            transaction.amount?.toString() || '',
            transaction.nmi_transactionid || '',
            transaction.nmi_transaction_id || '',
            transaction.orderid || '',
            transaction.email || '',
            transaction.category || '',
            transaction.sale_state || '',
            transaction.tfn || ''
          ].join(' ').toLowerCase();
          
          if (!searchableText.includes(searchTerm)) {
            return false;
          }
        }
      }

      // Created by filter
      if (filters.createdBy) {
        if (transaction.created_by !== filters.createdBy) {
          return false;
        }
      }

      // Transaction type filter
      if (filters.txType) {
        if (transaction.tx_type !== filters.txType) {
          return false;
        }
      }

      // Status filter
      if (filters.status) {
        if (transaction.status !== filters.status) {
          return false;
        }
      }

      // Processing Status filter
      if (filters.processingStatus) {
        const currentProcessingStatus = transaction.processing_status || 'PENDING_ASSIGNMENT';
        if (currentProcessingStatus !== filters.processingStatus) {
          return false;
        }
      }

      // Processed By filter (assigned processor)
      if (filters.processedBy) {
        const processedBy = (transaction as any).assigned_to_processor_name || (transaction as any).assigned_to_processor;
        if (!processedBy || processedBy !== filters.processedBy) {
          return false;
        }
      }

      // Processor Assigned By filter (who assigned the processor)
      if (filters.processorAssignedBy) {
        const assignedBy = (transaction as any).assigned_by_name || (transaction as any).assigned_by;
        if (!assignedBy || assignedBy !== filters.processorAssignedBy) {
          return false;
        }
      }

      // Amount filters
      if (transaction.amount !== undefined && transaction.amount !== null) {
        const transactionAmount = Number(transaction.amount);
        
        // Exact amount filter
        if (filters.amountExact) {
          const exactAmount = parseFloat(filters.amountExact);
          if (!isNaN(exactAmount) && Math.abs(transactionAmount - exactAmount) > 0.001) {
            return false;
          }
        }

        // Minimum amount filter
        if (filters.amountMin) {
          const minAmount = parseFloat(filters.amountMin);
          if (!isNaN(minAmount) && transactionAmount < minAmount) {
            return false;
          }
        }

        // Maximum amount filter
        if (filters.amountMax) {
          const maxAmount = parseFloat(filters.amountMax);
          if (!isNaN(maxAmount) && transactionAmount > maxAmount) {
            return false;
          }
        }
      }

      // Card brand filter
      if (filters.cardBrand) {
        if (transaction.card_brand?.toLowerCase() !== filters.cardBrand.toLowerCase()) {
          return false;
        }
      }

      // Last 4 digits filter
      if (filters.last4) {
        if (transaction.last4 !== filters.last4) {
          return false;
        }
      }

      // BIN (first 6) filter — masked_pan now starts with the BIN (e.g. "424242******4242")
      if (filters.bin) {
        const binDigits = filters.bin.replace(/\D/g, '');
        if (binDigits && !(transaction.masked_pan || '').startsWith(binDigits)) {
          return false;
        }
      }

      // Customer / billing filters
      const t = transaction as any;
      // DOB stored as MM/DD/YYYY — match each part independently
      if (filters.dobMonth && (t.date_of_birth || '').slice(0, 2) !== filters.dobMonth.padStart(2, '0')) return false;
      if (filters.dobDay && (t.date_of_birth || '').slice(3, 5) !== filters.dobDay.padStart(2, '0')) return false;
      if (filters.dobYear && (t.date_of_birth || '').slice(6, 10) !== filters.dobYear) return false;
      if (filters.email && !((t.email || '').toLowerCase().includes(filters.email.toLowerCase()))) return false;
      if (filters.dl && !((t.dl_last4 || '').toLowerCase().includes(filters.dl.toLowerCase()))) return false;
      if (filters.zip && !((t.zip || '').startsWith(filters.zip))) return false;
      if (filters.city && !((t.city || '').toLowerCase().includes(filters.city.toLowerCase()))) return false;
      if (filters.billingState && (t.state || '').toLowerCase() !== filters.billingState.toLowerCase()) return false;
      if (filters.address && !((t.address1 || '').toLowerCase().includes(filters.address.toLowerCase()))) return false;
      if (filters.phone && !((t.phone || '').toLowerCase().includes(filters.phone.toLowerCase()))) return false;

      // Last 4 SSN filter
      if (filters.last4Ssn) {
        if (transaction.last4_ssn !== filters.last4Ssn) {
          return false;
        }
      }

      // Response code filter
      if (filters.responseCode) {
        const responseCode = transaction.nmi_response_code || transaction.response_code || '';
        if (responseCode !== filters.responseCode) {
          return false;
        }
      }

      // Category (Sale Type) filter
      if (filters.category) {
        if (transaction.category !== filters.category) {
          return false;
        }
      }

      // Sale State filter
      if (filters.saleState) {
        if (transaction.sale_state !== filters.saleState) {
          return false;
        }
      }

      // TFN filter
      if (filters.tfn) {
        if (transaction.tfn !== filters.tfn) {
          return false;
        }
      }

      // Date range filters
      // (business days: 5 AM to 5 AM Pakistan time, same as the server filters)
      if ((filters.dateFrom || filters.dateTo) && !inBusinessDays(transaction.created_at, filters.dateFrom, filters.dateTo)) {
        return false;
      }

      // Void/Refund status filter
      if (filters.voidRefundStatus) {
        const isVoided = transaction.is_voided === true;
        const isRefunded = transaction.is_refunded === true;
        
        switch (filters.voidRefundStatus) {
          case 'active':
            if (isVoided || isRefunded) return false;
            break;
          case 'voided':
            if (!isVoided) return false;
            break;
          case 'refunded':
            if (!isRefunded) return false;
            break;
          case 'void_refund':
            if (!isVoided && !isRefunded) return false;
            break;
        }
      }

      // Voided/Refunded by filter
      if (filters.voidedRefundedBy) {
        const voidedBy = transaction.voided_by;
        const refundedBy = transaction.refunded_by;
        
        if (voidedBy !== filters.voidedRefundedBy && refundedBy !== filters.voidedRefundedBy) {
          return false;
        }
      }

      return true;
    });
  }, [transactions, filters, hasActiveFilters]);

  // Filter statistics
  const filterStats = useMemo(() => {
    return {
      total: transactions.length,
      filtered: filteredTransactions.length,
      hidden: transactions.length - filteredTransactions.length
    };
  }, [transactions.length, filteredTransactions.length]);

  return {
    filters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
    filteredTransactions,
    filterStats
  };
};

// Map the UI filter state to backend /paged query params (shared by Payment History,
// Rejected and Pending pages). Keys the backend doesn't support are simply ignored.
export function toBackendFilters(f: TransactionFilterState): Record<string, string> {
  return {
    search: f.globalSearch,
    created_by: f.createdBy,
    tx_type: f.txType,
    status: f.status,
    processing_status: f.processingStatus,
    card_brand: f.cardBrand,
    last4: f.last4,
    bin: f.bin,
    last4_ssn: f.last4Ssn,
    dob_year: f.dobYear,
    dob_month: f.dobMonth,
    dob_day: f.dobDay,
    email: f.email,
    dl: f.dl,
    zip: f.zip,
    city: f.city,
    billing_state: f.billingState,
    address: f.address,
    phone: f.phone,
    response_code: f.responseCode,
    category: f.category,
    sale_state: f.saleState,
    tfn: f.tfn,
    amount_exact: f.amountExact,
    amount_min: f.amountMin,
    amount_max: f.amountMax,
    date_from: f.dateFrom,
    date_to: f.dateTo,
    void_refund_status: f.voidRefundStatus,
    voided_refunded_by: f.voidedRefundedBy,
  };
}